import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";

import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { chamarMag } from "./funcoes";
import { formatBRL, formatBrDateTime } from "@/lib/format";
import {
  BadgeSeguradora,
  CampoBusca,
  CartaoTabela,
  ErroCarga,
  PageSpinner,
  TabelaVazia,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  casaDocumento,
  celula,
  formatarDocumento,
} from "./comuns";
import { DocumentosDaProposta } from "./cotacao/DocumentosDaProposta";
import { carregarPropostasPlataforma, documentosCompletos, type PropostaPlataforma } from "./propostasPlataforma";
import { CaixaPlaybook } from "../ai/playbook/CaixaPlaybook";
import { PropostaAzosDetalhe } from "./AcoesAzos";
import { useConexoesAzos } from "./conexaoAzos";
import { dataCurta } from "./portalAzos";
import { type PropostaPortal, rotuloProposta, usePortalAzos } from "./portalAzosAcoes";
import { IconePlaybook } from "../ai/playbook/BotaoPlaybook";

/**
 * As propostas feitas pela plataforma — na aba Propostas de Seguros e na
 * ficha do contato. Cada linha abre o detalhe: a situação ao vivo na MAG, os
 * documentos e as pendências. Quem vê o quê é a RLS (ver propostasPlataforma.ts).
 */

const SITUACAO_LOCAL: Record<PropostaPlataforma["status"], { rotulo: string; classe: string }> = {
  registrada: { rotulo: "Registrada", classe: "bg-ok-fundo text-ok" },
  reservada: { rotulo: "Registro não concluído", classe: "bg-alerta-fundo text-alerta" },
  erro: { rotulo: "Falha no registro", classe: "bg-destructive/10 text-destructive" },
};

const Situacao = ({ p }: { p: PropostaPlataforma }) => {
  // Status fora da lista não derruba a tabela: aparece cru, em cinza.
  const s = SITUACAO_LOCAL[p.status] ?? { rotulo: String(p.status ?? "—"), classe: "bg-muted text-muted-foreground" };
  return <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${s.classe}`}>{s.rotulo}</span>;
};

const Documentos = ({ p }: { p: PropostaPlataforma }) =>
  p.status !== "registrada" ? (
    <span className="text-xs text-muted-foreground">—</span>
  ) : documentosCompletos(p) ? (
    <span className="text-xs font-medium text-ok">Enviados</span>
  ) : (
    <span className="text-xs font-medium text-alerta">Faltam PDFs</span>
  );

interface PassoTimeline {
  data: string;
  status: string;
  motivo: string | null;
}

export const PropostaPlataformaDetalhe = ({
  proposta,
  aoFechar,
  aoAbrirPlaybook,
}: {
  proposta: PropostaPlataforma | null;
  aoFechar: () => void;
  /** O Sheet é modal: quem chama fecha ele e abre a caixinha (ver CaixaPlaybook). */
  aoAbrirPlaybook?: (p: PropostaPlataforma) => void;
}) => {
  const [timeline, setTimeline] = useState<PassoTimeline[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    setTimeline(null);
    setErro(null);
    if (!proposta || proposta.status !== "registrada") return;
    let ativo = true;
    chamarMag<{ timeline: PassoTimeline[] }>(
      { cotacaoId: proposta.cotacaoId, acao: "situacao" },
      "não foi possível consultar a situação na MAG",
    ).then(({ dados, erro: falha }) => {
      if (!ativo) return;
      if (falha) setErro(falha);
      else setTimeline(dados?.timeline ?? []);
    });
    return () => {
      ativo = false;
    };
  }, [proposta]);

  return (
    <Sheet open={!!proposta} onOpenChange={(aberto) => !aberto && aoFechar()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
        {proposta && (
          <>
            <SheetHeader>
              <SheetTitle>Proposta {proposta.numero}</SheetTitle>
              <SheetDescription>
                {proposta.contatoNome ?? "—"}
                {proposta.vendedorNome ? ` · vendida por ${proposta.vendedorNome}` : ""}
              </SheetDescription>
              {aoAbrirPlaybook && (
                <Button type="button" variant="outline" size="sm" className="mt-2 self-start" onClick={() => aoAbrirPlaybook(proposta)}>
                  <IconePlaybook className="text-primary" />
                  Playbook
                </Button>
              )}
            </SheetHeader>

            <section className="flex flex-col gap-2 px-4">
              <h3 className="text-sm font-semibold">Situação na MAG</h3>
              {proposta.status !== "registrada" ? (
                <p className="text-sm text-muted-foreground">
                  {proposta.erro ?? "O registro não foi concluído."} Reenvio pela cotação, com o mesmo número reservado.
                </p>
              ) : erro ? (
                <ErroCarga oQue="a situação na MAG" erro={erro} />
              ) : timeline === null ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : timeline.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhum histórico na MAG.</p>
              ) : (
                <ol className="divide-y">
                  {[...timeline].reverse().map((t, i) => (
                    <li key={i} className="flex items-start justify-between gap-3 py-2 text-sm">
                      <div>
                        <span className={i === 0 ? "font-semibold" : ""}>{t.status}</span>
                        {t.motivo && <p className="text-xs text-muted-foreground">{t.motivo}</p>}
                      </div>
                      <span className="shrink-0 text-xs text-muted-foreground">{formatBrDateTime(t.data)}</span>
                    </li>
                  ))}
                </ol>
              )}
            </section>

            {proposta.status === "registrada" && (
              <section className="px-4 pb-6">
                <div className="rounded-[14px] border border-[#bbf7d0] bg-[#f0fdf4] px-4 pb-3">
                  <DocumentosDaProposta cotacaoId={proposta.cotacaoId} documentosIniciais={proposta.documentos} />
                </div>
              </section>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

type Linha = { tipo: "plataforma"; data: string; p: PropostaPlataforma } | { tipo: "azos"; data: string; p: PropostaPortal };

/**
 * Lista com busca; `contatoId` restringe à pessoa (ficha do contato) e tira as colunas redundantes.
 * Fora da ficha, entram também as propostas da Azos da própria conexão (lidas
 * na hora do portal), com as ações da proposta no detalhe.
 */
export const ListaPropostasPlataforma = ({
  contatoId,
  comTitulo = false,
}: {
  contatoId?: number | string;
  comTitulo?: boolean;
}) => {
  const [linhas, setLinhas] = useState<PropostaPlataforma[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [busca, setBusca] = useState("");
  const [detalhe, setDetalhe] = useState<PropostaPlataforma | null>(null);
  const [playbook, setPlaybook] = useState<PropostaPlataforma | null>(null);
  const [detalheAzos, setDetalheAzos] = useState<PropostaPortal | null>(null);
  const naFicha = contatoId != null;
  const { propriaConectada } = useConexoesAzos();
  const azos = usePortalAzos<PropostaPortal>("propostas", !naFicha && propriaConectada != null);

  useEffect(() => {
    let ativo = true;
    carregarPropostasPlataforma(contatoId)
      .then((l) => ativo && setLinhas(l))
      .catch((e) => ativo && setErro(e instanceof Error ? e.message : String(e)));
    return () => {
      ativo = false;
    };
  }, [contatoId]);

  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const daPlataforma = (linhas ?? []).filter(
      (p) =>
        !termo ||
        p.contatoNome?.toLowerCase().includes(termo) ||
        p.vendedorNome?.toLowerCase().includes(termo) ||
        p.numero.includes(termo) ||
        casaDocumento(p.cpf, termo),
    );
    const daAzos = (azos.data ?? []).filter((p) => !termo || p.segurado?.toLowerCase().includes(termo));
    return [
      ...daPlataforma.map((p): Linha => ({ tipo: "plataforma", data: p.enviadaEm ?? "", p })),
      ...daAzos.map((p): Linha => ({ tipo: "azos", data: p.criada_em ?? "", p })),
    ].sort((x, y) => y.data.localeCompare(x.data));
  }, [linhas, azos.data, busca]);
  const total = (linhas?.length ?? 0) + (azos.data?.length ?? 0);

  if (erro) return <ErroCarga oQue="as propostas" erro={erro} />;
  if (!linhas) return naFicha ? null : <PageSpinner />;
  if (naFicha && linhas.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      {comTitulo && <h3 className="text-lg font-semibold">Propostas de seguro</h3>}
      {!naFicha && (
        <div className="flex flex-wrap items-center gap-2">
          <CampoBusca valor={busca} aoMudar={setBusca} placeholder="Buscar por cliente, CPF, vendedor ou número" />
          <span className="ml-auto text-xs text-muted-foreground">
            {visiveis.length} de {total}
          </span>
        </div>
      )}
      {azos.error && <p className="text-xs text-destructive">{(azos.error as Error).message}</p>}
      <CartaoTabela>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{naFicha ? "Proposta" : "Cliente"}</TableHead>
              <TableHead>Seguradora</TableHead>
              <TableHead>Situação</TableHead>
              <TableHead>Documentos</TableHead>
              <TableHead className="text-right">Prêmio mensal</TableHead>
              <TableHead>Enviada</TableHead>
              {!naFicha && <TableHead>Vendedor</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visiveis.length === 0 ? (
              <TabelaVazia
                colunas={naFicha ? 6 : 7}
                mensagem="Nenhuma proposta."
              />
            ) : (
              visiveis.map((l) => {
                if (l.tipo === "azos") {
                  const p = l.p;
                  return (
                    <TableRow
                      key={`azos:${p.id}`}
                      className="cursor-pointer"
                      onClick={() => setDetalheAzos(p)}
                      title="Abrir situação e ações na seguradora"
                    >
                      <TableCell className={celula}>
                        <div className="font-medium">{p.segurado ?? "—"}</div>
                        <div className="text-xs text-muted-foreground">proposta no portal da Azos</div>
                      </TableCell>
                      <TableCell className={celula}>
                        <BadgeSeguradora codigo="azos" />
                      </TableCell>
                      <TableCell className={celula}>
                        <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                          {rotuloProposta(p)}
                        </span>
                      </TableCell>
                      <TableCell className={celula}>
                        <span className="text-xs text-muted-foreground">—</span>
                      </TableCell>
                      <TableCell className={`${celula} text-right`}>{p.premio != null ? formatBRL(p.premio) : "—"}</TableCell>
                      <TableCell className={`${celula} text-xs`}>{dataCurta(p.criada_em)}</TableCell>
                      {!naFicha && <TableCell className={`${celula} text-xs`}>—</TableCell>}
                    </TableRow>
                  );
                }
                const p = l.p;
                return (
                  <TableRow
                    key={`plataforma:${p.seguradora}:${p.numero}`}
                    className="cursor-pointer"
                    onClick={() => setDetalhe(p)}
                    title="Abrir situação, documentos e pendências"
                  >
                    <TableCell className={celula}>
                      {naFicha ? (
                        <div className="font-medium">nº {p.numero}</div>
                      ) : (
                        <>
                          <div className="font-medium">{p.contatoNome ?? "—"}</div>
                          <div className="text-xs text-muted-foreground">
                            {formatarDocumento(p.cpf)} · nº {p.numero}
                          </div>
                        </>
                      )}
                    </TableCell>
                    <TableCell className={celula}>
                      <BadgeSeguradora codigo={p.seguradora} />
                    </TableCell>
                    <TableCell className={celula}>
                      <Situacao p={p} />
                    </TableCell>
                    <TableCell className={celula}>
                      <Documentos p={p} />
                    </TableCell>
                    <TableCell className={`${celula} text-right`}>{p.premioMensal != null ? formatBRL(p.premioMensal) : "—"}</TableCell>
                    <TableCell className={`${celula} text-xs`}>{p.enviadaEm ? formatBrDateTime(p.enviadaEm) : "—"}</TableCell>
                    {!naFicha && <TableCell className={`${celula} text-xs`}>{p.vendedorNome ?? "—"}</TableCell>}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </CartaoTabela>
      <PropostaPlataformaDetalhe
        proposta={detalhe}
        aoFechar={() => setDetalhe(null)}
        aoAbrirPlaybook={(p) => {
          setDetalhe(null);
          setPlaybook(p);
        }}
      />
      {propriaConectada && (
        <PropostaAzosDetalhe proposta={detalheAzos} crmUserId={propriaConectada.crm_user_id} aoFechar={() => setDetalheAzos(null)} />
      )}
      {playbook && (
        <CaixaPlaybook
          key={playbook.cotacaoId}
          alvo={{ tipo: "seguro", id: playbook.cotacaoId, nome: playbook.contatoNome }}
          aoFechar={() => setPlaybook(null)}
        />
      )}
    </div>
  );
};

/** Bloco da ficha do contato: some quando a pessoa não tem proposta feita pela plataforma. */
export const PropostasDoContato = ({ contatoId }: { contatoId: number | string }) => (
  <div className="mb-6 flex flex-col gap-2 empty:hidden">
    <ListaPropostasPlataforma contatoId={contatoId} comTitulo />
  </div>
);
